import type { AxisLensReadRecord } from '../lib/api';
import styles from './AxisLensCard.module.css';

/** Humanize an axis_key ('executive-communication') into display text
 *  ('Executive communication') — same house pattern as radarPath.ts's
 *  humanizeAxisLabel, kept as its own small local copy rather than importing
 *  a component from a different concept's module (RadarChart's radarPath.ts
 *  is the FIT-RADAR's own helper; this card intentionally does not import
 *  from it — see AxisLensService.ts's header on why the two "axes" concepts
 *  never share code or copy, only a similar cosmetic humanize step).
 *
 *  Exported (AXIS-VIZ) so JobCard's list-row glance chip can reuse the exact
 *  same cosmetic step for the SAME lens concept — this is not the
 *  RadarChart/role_axes boundary the comment above guards; both call sites
 *  here are role_axes-concept renders of the identical served axisKey. */
export function humanizeAxisKey(key: string): string {
  const spaced = key.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : spaced;
}

/** Exported (AXIS-VIZ) — same reuse rationale as humanizeAxisKey above. */
export const REGISTER_LABEL: Record<string, string> = {
  intern: 'Intern / new grad',
  early: 'Early career',
  mid: 'Mid-level',
  senior: 'Senior',
  exec: 'Executive',
};

/** AXIS-VIZ (R299 follow-up, owner's ruling verbatim: "it would be good to
 *  show a visual indicator of what's being derived — like the match score
 *  for a position"). The lens's read used to live ONLY inside the collapsed
 *  "How this role reads" details card — invisible until a tap. This is the
 *  always-visible compact signature that sits near the score area: the
 *  TOP 3 weights as small labeled bars + the register chip + the honest
 *  coverage caveat, nothing else. The full weight list + matchedCues
 *  receipts stay exclusively in AxisLensCard (rendered below, inside the
 *  existing collapsible) — this component is a SUMMARY, never a duplicate
 *  full render.
 *
 *  SCORE-RING-CLASS PROMINENCE, WITHOUT BORROWING THE RADAR'S LANGUAGE: the
 *  brief is explicit that this must not be confusable with the FitRadar (the
 *  six-key candidate-FIT verdict, RadarChart.tsx) — so no spider/radar shape
 *  here, just labeled horizontal bars (the same bar idiom AxisLensCard's own
 *  full render already uses, just capped to 3 and laid out compactly). Bars
 *  ride var(--tl-accent) exclusively (ONE ramp, tokens-only) — never
 *  --tl-good/--tl-warm/--tl-error, which are reserved for semantic
 *  good/warn/bad or "honest extra fact" reads elsewhere on this page (the
 *  relocation toggle, the tracking/sponsorship chips). The register chip
 *  uses the SAME --tl-accent-soft/--tl-accent pairing as JobCard's own
 *  bucket-chip idiom, just a neutral (non-bucket) tint — a role-register
 *  read is not a bucket decision, so it must not borrow a bucket's own hue.
 *
 *  NO INVENTION: `read === null` covers BOTH "no key configured" and "a
 *  derivation is still in flight" — this renders NOTHING extra by default
 *  (the score card above already fills that space); `isLoading` is the ONLY
 *  path that renders an unobtrusive "reading…" line, and only while a fetch
 *  is genuinely in flight (never a guessed/placeholder bar). A resolved-but-
 *  empty read (weights.length === 0, e.g. a JD too thin to trace anything)
 *  renders the register chip alone plus the honest low-coverage tag — never
 *  a fabricated bar. */
/** A single bar in the compact signature — width already resolved to a
 *  0..100 percent, label already humanized. Pure display data, no JSX. */
export interface AxisSignatureBar {
  axisKey: string;
  label: string;
  widthPct: number;
}

/** The compact signature's fully-resolved render state — one honest variant
 *  per case AxisLensSignature can be in. Extracted as its OWN pure function
 *  (mirrors Onboarding.tsx's describeDiscoverResult precedent: this monorepo
 *  tests component STATE via a pure derivation function, not DOM rendering —
 *  no jsdom/@testing-library is wired into any vitest config here) so every
 *  branch is independently unit-testable with plain data, zero React. */
export type AxisSignatureView =
  | { kind: 'none' }
  | { kind: 'loading' }
  | {
      kind: 'read';
      registerLabel: string;
      unusualTag: 'too thin to read fully' | 'reads unusual' | null;
      bars: AxisSignatureBar[];
      emptyNote: string | null;
      /** H2 FIX-D (docs/qa/FIX-PLAN-H2-axisread-starvation-2026-07-10.md) —
       *  ONE composed thin-state container groups these three named cases
       *  (replaces the old three-stacked-fragments render: a filled register
       *  pill + a floating italic 'too thin' tag + a separate bare empty
       *  line, three visual languages for what is honestly one caveat).
       *  null is the untouched normal path — a confidently-covered read, or
       *  'reads_unusual' with bars present (neither is one of the three
       *  named cases; see the fix plan). */
      thinState: 'jd_too_thin' | 'unstructured_read' | 'empty' | null;
      /** The composed block's own caption line — copy lives here, not in
       *  JSX, same convention as unusualTag/emptyNote above. null when
       *  thinState is 'empty' (emptyNote already carries the honest message)
       *  or null (no thin state at all). */
      thinCaption: string | null;
    };

/** Pure: read + isLoading -> the exact view AxisLensSignature renders. See
 *  AxisSignatureView's own doc comment for why this is split out. */
export function deriveAxisSignatureView(
  read: AxisLensReadRecord | null,
  isLoading: boolean,
): AxisSignatureView {
  if (!read) {
    return isLoading ? { kind: 'loading' } : { kind: 'none' };
  }

  const topWeights = read.weights.slice(0, 3);
  const registerLabel = REGISTER_LABEL[read.register] ?? read.register;
  const coveragePct = Math.round(read.coverage * 100);
  const unusualTag: 'too thin to read fully' | 'reads unusual' | null =
    read.coverageNote === 'jd_too_thin'
      ? 'too thin to read fully'
      : read.coverageNote === 'reads_unusual'
        ? 'reads unusual'
        : null;

  const bars: AxisSignatureBar[] = topWeights.map((w) => ({
    axisKey: w.axisKey,
    label: humanizeAxisKey(w.axisKey),
    widthPct: Math.round(Math.max(0, Math.min(1, w.weight)) * 100),
  }));

  const emptyNote =
    bars.length > 0
      ? null
      : coveragePct === 0
        ? "Nothing traced clearly yet — see the full read below."
        : `Only ${coveragePct}% traced — see the full read below.`;

  // H2 FIX-D — one named thin-state per the plan's three composed cases.
  // null (the untouched normal path) whenever none apply: a confidently-
  // covered read, or 'reads_unusual' with bars present (that combination is
  // NOT one of the three named cases — it keeps today's chip+tag rendering).
  const thinState: 'jd_too_thin' | 'unstructured_read' | 'empty' | null =
    read.coverageNote === 'jd_too_thin'
      ? 'jd_too_thin'
      : read.coverageNote === 'unstructured_read'
        ? 'unstructured_read'
        : bars.length === 0
          ? 'empty'
          : null;

  // Copy lives here (pure function), same convention as unusualTag/emptyNote
  // above — JSX never hardcodes this text. Lowercase, phrase-not-sentence
  // casing intentionally matches unusualTag's own register (these read as
  // small tags, not prose) — 'too thin to read fully' is literally the SAME
  // fact unusualTag already names, now composed inside the block instead of
  // floating beside the chip; 'read from the posting's description' is the
  // brief's own suggested copy for the incoming 'unstructured_read'
  // coverageNote (Lane 1's JD-source seam).
  const thinCaption: string | null =
    thinState === 'jd_too_thin'
      ? 'too thin to read fully'
      : thinState === 'unstructured_read'
        ? "read from the posting's description"
        : null;

  return { kind: 'read', registerLabel, unusualTag, bars, emptyNote, thinState, thinCaption };
}

/** The traced-axis bar list — shared verbatim between the normal-path render
 *  and the H2 FIX-D composed thin-state block below (same rows, same
 *  markup; only their container differs). Extracted so the two call sites
 *  can never silently drift apart. */
function SigBarList({ bars }: { bars: AxisSignatureBar[] }) {
  return (
    <div className={styles.sigBars}>
      {bars.map((bar) => (
        <div key={bar.axisKey} className={styles.sigBarRow}>
          <span className={styles.sigBarLabel}>{bar.label}</span>
          <div className={styles.sigBarTrack}>
            <div className={styles.sigBarFill} style={{ width: `${bar.widthPct}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function AxisLensSignature({
  read,
  isLoading = false,
}: {
  read: AxisLensReadRecord | null;
  isLoading?: boolean;
}) {
  const view = deriveAxisSignatureView(read, isLoading);

  if (view.kind === 'none') return null;
  if (view.kind === 'loading') {
    return <div className={styles.sigLoading}>Reading how this role reads…</div>;
  }

  // H2 FIX-D — ONE composed thin-state block for exactly the three named
  // cases (jd_too_thin / unstructured_read / a genuinely empty read): one
  // container, one visual language, the register pill living inside it.
  // Replaces the old three-fragment stack (chip row + floating tag +
  // separate bare empty line). Every other read (a confident read, or
  // reads_unusual with bars present) falls through to the untouched render
  // below, byte-identical to before this fix.
  if (view.thinState) {
    return (
      <div className={styles.sigWrap}>
        <div className={styles.sigThinBlock}>
          <span className={styles.sigRegisterChip}>{view.registerLabel}</span>
          {view.thinCaption ? (
            <div className={styles.sigThinCaption}>{view.thinCaption}</div>
          ) : null}
          {view.bars.length > 0 ? (
            <SigBarList bars={view.bars} />
          ) : (
            <div className={styles.sigThinEmptyNote}>{view.emptyNote}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.sigWrap}>
      <div className={styles.sigTopRow}>
        <span className={styles.sigRegisterChip}>{view.registerLabel}</span>
        {view.unusualTag ? <span className={styles.sigUnusualTag}>{view.unusualTag}</span> : null}
      </div>
      {view.bars.length > 0 ? (
        <SigBarList bars={view.bars} />
      ) : (
        <div className={styles.sigEmpty}>{view.emptyNote}</div>
      )}
    </div>
  );
}

/* D14 (2026-07-08 live-test): "How this role reads gives us thirty-five
 * percent, both starting from the left... it should read more like a Gantt
 * chart — linear, and fills in each one. If they all start from the left at
 * zero, it doesn't make sense." The weights are COMPOSITIONAL — parts of ONE
 * read of the role — so the render must compose them into one bar, not N
 * independent bars each restarting at zero.
 *
 * ONE segment per traced axis in the composite bar (SegmentedBarSegment
 * below); each fills in immediately after the previous one's right edge —
 * true Gantt-style stacking, not a percent-of-track split. The untraced
 * remainder (100% minus the sum of traced weights, floored at 0 — weights
 * are already clamped 0..1 individually but a sum can still legitimately
 * fall short of 100) renders as its OWN honest, visibly-hatched final
 * segment, labeled with the same wording the existing jd_too_thin note
 * already uses ("too thin to read with confidence") — this is that SAME
 * honesty principle now applied to the visual composition, not just the
 * copy underneath it. */
export interface SegmentedBarSegment {
  axisKey: string;
  label: string;
  /** 0..100, this segment's OWN width — segments are laid out left-to-right
   *  in order, each starting where the previous one ended (CSS flex-basis
   *  achieves this natively; no explicit offset math needed in the DOM). */
  widthPct: number;
}

export interface ComposedAxisBarView {
  segments: SegmentedBarSegment[];
  /** 0..100 — the honest untraced remainder. Zero when the traced weights
   *  already sum to (or exceed) 100%; never negative. */
  untracedPct: number;
}

/** Pure: read.weights -> the ONE composed bar's segment list + untraced
 *  remainder. Caps to the SAME top-4 the card already showed as independent
 *  bars (no behavior change to which axes render, only how). Segment widths
 *  are each axis's OWN weight, clamped 0..1 same as before; the untraced
 *  remainder is 100 minus their sum, floored at 0 (a sum that already
 *  reaches/exceeds 100 — every traced axis fully accounted for — renders NO
 *  hatched segment; nothing untraced to honestly report). */
export function deriveComposedAxisBarView(weights: AxisLensReadRecord['weights']): ComposedAxisBarView {
  const topWeights = weights.slice(0, 4);
  const segments: SegmentedBarSegment[] = topWeights.map((w) => ({
    axisKey: w.axisKey,
    label: humanizeAxisKey(w.axisKey),
    widthPct: Math.round(Math.max(0, Math.min(1, w.weight)) * 100),
  }));
  const tracedSum = segments.reduce((sum, s) => sum + s.widthPct, 0);
  const untracedPct = Math.max(0, 100 - tracedSum);
  return { segments, untracedPct };
}

export function AxisLensCard({ read }: { read: AxisLensReadRecord | null }) {
  if (!read) {
    return <div className={styles.empty}>Not available for this posting yet.</div>;
  }

  const registerLabel = REGISTER_LABEL[read.register] ?? read.register;
  const coveragePct = Math.round(read.coverage * 100);
  const composed = deriveComposedAxisBarView(read.weights);

  return (
    <div className={styles.wrap}>
      <div className={styles.registerRow}>
        <span className={styles.registerLabel}>Reads as</span>
        <span className={styles.registerValue}>{registerLabel}</span>
      </div>

      {composed.segments.length > 0 ? (
        <div className={styles.composedBarBlock}>
          {/* THE ONE COMPOSED BAR — each traced axis is a segment filling in
              after the previous one (flexBasis order = visual order), the
              untraced remainder is its own hatched, honestly-labeled final
              segment. role="img" + an aria-label describing the composition
              in words, since the segment proportions themselves carry no
              individually-focusable text (the legend rows below are the
              accessible per-axis detail). */}
          <div
            className={styles.composedBarTrack}
            role="img"
            aria-label={`How this role reads: ${composed.segments
              .map((s) => `${s.label} ${s.widthPct}%`)
              .join(', ')}${composed.untracedPct > 0 ? `, ${composed.untracedPct}% untraced` : ''}`}
          >
            {composed.segments.map((s) => (
              <div
                key={s.axisKey}
                className={styles.composedBarSegment}
                style={{ flexBasis: `${s.widthPct}%` }}
              />
            ))}
            {composed.untracedPct > 0 ? (
              <div
                className={styles.composedBarUntraced}
                style={{ flexBasis: `${composed.untracedPct}%` }}
              />
            ) : null}
          </div>

          {/* The legend — one row per segment IN THE SAME ORDER as the bar,
              so a viewer can read left-to-right down the list exactly as the
              bar fills left-to-right. This replaces the old independent
              bars-with-percent rows one-for-one; same label/value shape,
              now describing a segment of the ONE bar above rather than its
              own zero-anchored bar. */}
          <div className={styles.bars}>
            {composed.segments.map((s) => (
              <div key={s.axisKey} className={styles.barRow}>
                <span className={styles.composedLegendSwatch} aria-hidden="true" />
                <span className={styles.barLabel}>{s.label}</span>
                <span className={styles.barValue}>{s.widthPct}%</span>
              </div>
            ))}
            {composed.untracedPct > 0 ? (
              <div className={styles.barRow}>
                <span
                  className={`${styles.composedLegendSwatch} ${styles.composedLegendSwatchUntraced}`}
                  aria-hidden="true"
                />
                <span className={styles.barLabel}>Untraced — too thin to read with confidence</span>
                <span className={styles.barValue}>{composed.untracedPct}%</span>
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div className={styles.empty}>
          Nothing in this posting's own text traced clearly to a known axis yet.
        </div>
      )}

      {read.coverageNote === 'jd_too_thin' ? (
        <div className={styles.note}>
          This posting's requirements are too thin to read with confidence — showing what little
          traced, not a guess.
        </div>
      ) : read.coverageNote === 'reads_unusual' ? (
        <div className={styles.note}>
          This role reads a bit unusual against what I know so far ({coveragePct}% of its
          requirements traced) — not a forced fit, just an honest partial read.
        </div>
      ) : (
        <div className={styles.note}>{coveragePct}% of this posting's requirements traced.</div>
      )}
    </div>
  );
}
